/*
XOL-67 follow-up (decisions 193 to 202): the "Site handover" block of the Step 3 sharer form (LicenseeForm), in pure functions.

An RTA's Complete opens an RTS, and a Telco may keep its Candidate site until that RTS completes. So the sharer form of THAT RTS,
Create and Modify, can set the site's `handover` and `handoverStatus` in the same API write as the sharer (`siteTransition`).
Plan: docs/plans/2026-10-29-xol-67-optional-handover.md.

  Who sees it (the host decides, with these)
    offersTransition(site, mode)               create: a Candidate site with no handover date; modify: a Candidate site
    otherLiveSharers(peers, selfId)            how many OTHER sharers on the site are not Terminated (Expired counts: decision 201)

  The block's state { on, followSharer, handover, handoverStatus }
    initialState(site, mode, sharerHandover)
    follow(state, sharerHandover)              while following, the site date IS the sharer's Handover Date (decision 193)
    sameAsSite(state, site)                    a ticked block that equals the site's own values is not a change (UX A2)
    errors(state)                              { handover?, handoverStatus? } when ticked; both are required
    payload(state, formatDateSql, site, mode)  the `siteTransition` body, or null when nothing is to be sent
    serverErrors(map)                          a 422 `siteTransition.*` map -> { handover?, handoverStatus? }

  Copy: refusalCopy, successSentence.

Dates are 'YYYY-MM-DD' strings and are only ever compared as strings, never parsed (XOL-414). ES5: no optional chaining or nullish
coalescing (Babel 6).
------------------------------------------------------------------------ */

import { CANDIDATE_SITE, HANDOVER_TRANSITION_STATUSES } from '@/components/Data'

// The API's refusals of the block (the whole save is rolled back; nothing was created or changed). Decision 202: the API does not
// refuse "another sharer".
export var SITE_NOT_CANDIDATE = 'site_not_candidate'
export var SITE_HANDOVER_SET = 'site_handover_set'
export var SITE_TRANSITION_NOT_RTA = 'site_transition_not_rta'
export var REFUSAL_CODES = [SITE_NOT_CANDIDATE, SITE_HANDOVER_SET, SITE_TRANSITION_NOT_RTA]

export var STATUSES = HANDOVER_TRANSITION_STATUSES

function blank (v) {
  return v === null || v === undefined || String(v).trim() === ''
}

// 'YYYY-MM-DD' of a date string, or ''. A plain slice: a date is never parsed here (XOL-414). A stored zero date ('0000-00-00') is no
// date, as on the API (DateAnchor): it comes back '' too, so every "has a date?" below reads it as blank.
export function ymd (v) {
  var d = blank(v) ? '' : String(v).slice(0, 10)

  return d === '0000-00-00' ? '' : d
}

function isCandidate (site) {
  return !!site && site.status === CANDIDATE_SITE
}

// The status the site already has scheduled, when it is one of the four; else null (a stored value outside them counts as none).
export function storedStatus (site) {
  var s = site ? site.handoverStatus : null

  return !blank(s) && STATUSES.indexOf(s) !== -1 ? s : null
}

// Create offers it for a Candidate site that has no handover date yet; Modify for any Candidate site, with or without a date.
export function offersTransition (site, mode) {
  if (!isCandidate(site)) return false

  return mode === 'modify' ? true : ymd(site.handover) === ''
}

// Decision 199 (as counted by decision 201): sharers other than `selfId` whose status is not Terminated. An Expired sharer counts
// and so does one with no status. The SPA display rule only (decision 202).
export function otherLiveSharers (peers, selfId) {
  var self = blank(selfId) ? null : Number(selfId)
  var n = 0

  ;(peers || []).forEach(function (p) {
    if (!p) return
    if (self !== null && Number(p.id) === self) return
    if (String(p.status || '').trim().toLowerCase() === 'terminated') return

    n++
  })

  return n
}

export function initialState (site, mode, sharerHandover) {
  var s = site || {}

  return {
    on: false,
    followSharer: true,
    handover: (mode === 'modify' && ymd(s.handover) !== '') ? ymd(s.handover) : (sharerHandover || null),
    handoverStatus: storedStatus(s)
  }
}

// While following, the site date is the sharer's Handover Date, or nothing. Returns the state it was given.
export function follow (state, sharerHandover) {
  if (state.followSharer) {
    state.handover = sharerHandover || null
  }

  return state
}

// UX A2: ticked, with a date and a status that are the site's own. Compared as strings.
export function sameAsSite (state, site) {
  if (!state || !state.on || !site) return false
  if (ymd(state.handover) === '' || blank(state.handoverStatus)) return false

  return ymd(state.handover) === ymd(site.handover) && state.handoverStatus === storedStatus(site)
}

export var MSG_FOLLOW_EMPTY = 'Set the sharer\'s Handover Date above, or untick Same as the sharer\'s Handover Date and pick a date.'
export var MSG_PICK_DATE = 'Pick the site\'s handover date.'
export var MSG_PICK_STATUS = 'Select the site\'s handover status.'

// Only while ticked; both fields are required.
export function errors (state) {
  var out = {}

  if (!state || !state.on) return out

  if (blank(state.handover)) {
    out.handover = state.followSharer ? MSG_FOLLOW_EMPTY : MSG_PICK_DATE
  }

  if (blank(state.handoverStatus) || STATUSES.indexOf(state.handoverStatus) === -1) {
    out.handoverStatus = MSG_PICK_STATUS
  }

  return out
}

// The `siteTransition` body: always both keys, the date explicit (the API never infers it from the licensee). Null when the block is
// off, or (Modify) when it says what the site already says.
export function payload (state, formatDateSql, site, mode) {
  if (!state || !state.on) return null
  if (mode === 'modify' && sameAsSite(state, site)) return null

  return {
    handover: formatDateSql(state.handover),
    handoverStatus: state.handoverStatus
  }
}

// A 422 validation map (`{ 'siteTransition.handover': ['...'] }`) -> the matching fields, with the API's first message.
export function serverErrors (map) {
  var out = {}

  if (!map || typeof map !== 'object') return out

  ;['handover', 'handoverStatus'].forEach(function (key) {
    var m = map['siteTransition.' + key]
    var first = Array.isArray(m) ? m[0] : m

    if (typeof first === 'string' && first) out[key] = first
  })

  return out
}

function siteWord (siteName) {
  return blank(siteName) ? 'The site' : String(siteName)
}

/**
 * A refusal of the block, in the host's slot: { title, description, notify } or null for a code that is not one. `notify` is the
 * `$notify` message (title "Not saved"); site_transition_not_rta is a bug signal that PostMixins toasts itself, so it has none.
 */
export function refusalCopy (code, mode, siteName) {
  var name = siteWord(siteName)
  var again = mode === 'modify' ? 'Press Save again to save the sharer without it.' : 'Press Save again to add the sharer without it.'

  if (code === SITE_HANDOVER_SET) {
    return {
      title: 'The site\'s handover is already set',
      description: 'Someone set ' + name + '\'s handover date while this form was open, so nothing was saved. ' + again,
      notify: 'The site\'s handover was set by someone else. Nothing was saved; see the form.'
    }
  }

  if (code === SITE_NOT_CANDIDATE) {
    return {
      title: 'The site is no longer a Candidate site',
      description: name + ' has changed status, so its handover can\'t be ' + (mode === 'modify' ? 'updated' : 'set') + ' here and nothing was saved. ' + again,
      notify: 'The site is no longer a Candidate site. Nothing was saved; see the form.'
    }
  }

  if (code === SITE_TRANSITION_NOT_RTA) {
    return {
      title: 'The site handover can\'t be set from this request',
      description: 'This request wasn\'t opened by an RTA, so nothing was saved. ' + again,
      notify: null
    }
  }

  return null
}

/**
 * The success message. `sent` is the body that was sent (or null). Create: "Sharer created and linked to this request." Modify: "Sharer
 * changes saved." when the sharer changed, else "Site handover saved." With a body sent, one more sentence: "The site becomes {Status} on
 * {date}.", or "... at the next nightly run." for today or earlier (decision 86; strings compared, `today` is 'YYYY-MM-DD').
 */
export function successSentence (mode, sharerModified, sent, today, formatDate) {
  var lead = 'Sharer created and linked to this request.'

  if (mode === 'modify') {
    // "Site handover saved." only when the site is all that changed.
    lead = sharerModified || !sent ? 'Sharer changes saved.' : 'Site handover saved.'
  }

  if (!sent || blank(sent.handover) || blank(sent.handoverStatus)) return lead

  var past = !blank(today) && ymd(sent.handover) <= String(today)

  return lead + ' The site becomes ' + sent.handoverStatus + (past ? ' at the next nightly run.' : ' on ' + formatDate(sent.handover) + '.')
}
